import { TotalResults } from '@shared/components/ui/TotalResults';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal/ConfirmModal';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { usePreInvoicesMain } from '../../hooks/usePreInvoicesMain';
import { useInvoicePreInvoice } from '../../hooks/useInvoicePreInvoice';
import { useBulkInvoicePreInvoices } from '../../hooks/useBulkInvoicePreInvoices';
import { useDeletePreInvoice } from '../../hooks/useDeletePreInvoice';
import { useMergePreInvoices } from '../../hooks/useMergePreInvoices';
import { PreInvoicesAgGridTable } from '../display';
import { Breadcrumb } from '../display/Breadcrumb';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection } from '../interaction';
import { useInvoicesPermissions } from '@modules/Invoices/InvoicesOrders/List/hooks/useInvoicesPermissions';

export const Main = () => {
  const { t } = useTranslation('preInvoicesModule');
  const navigate = useNavigate();
  const confirmInvoiceModalRef = useRef<ConfirmModalRef>(null);
  const confirmBulkInvoiceModalRef = useRef<ConfirmModalRef>(null);
  const confirmDeleteModalRef = useRef<ConfirmModalRef>(null);
  const confirmMergeModalRef = useRef<ConfirmModalRef>(null);

  const { invoicePreInvoice } = useInvoicePreInvoice();
  const { bulkInvoicePreInvoices } = useBulkInvoicePreInvoices();
  const { deletePreInvoice } = useDeletePreInvoice();
  const { mergePreInvoices } = useMergePreInvoices();

  // Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Hook que maneja toda la lógica de negocio de prefacturas
  const {
    // Estados
    selectedPreInvoices,
    selectedColumns,

    // Datos
    preInvoices,
    isLoading,
    isFetching,
    error,
    total,

    // Props para componentes
    preInvoicesViewProps,

    // Handlers
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleClearSelection,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Handlers de selección
    handleSelectionChanged,

    // Estado de filtros
    hasActiveFilters,
    filterConditions,
    searchTerm,
  } = usePreInvoicesMain();

  const { canCreateInvoices } = useInvoicesPermissions();

  // 🎯 Efecto para desactivar el spinner de carga inicial después del primer fetch
  useEffect(() => {
    // Si isLoading pasa de true a false, significa que la carga inicial terminó.
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // 🎯 Handler para ver detalle de prefactura
  const handleViewPreInvoice = useCallback(
    (preInvoiceId: number) => {
      navigate(`/invoices/pre-invoices/view/${preInvoiceId}`);
    },
    [navigate],
  );

  // 🎯 Handler para facturar prefactura individual (marcar como CLOSED)
  const handleInvoicePreInvoice = useCallback(
    (preInvoiceId: number) => {
      if (!canCreateInvoices) {
        return;
      }
      // Mostrar modal de confirmación antes de facturar
      confirmInvoiceModalRef.current?.open(
        () => {
          // Usuario confirmó: ejecutar facturación
          invoicePreInvoice(preInvoiceId, () => {
            // Refetch de datos después de la facturación exitosa
            refetch();
          });
        },
        undefined,
        t('confirmModals.invoicePreInvoice.title', 'Facturar prefactura'),
        t('confirmModals.invoicePreInvoice.message', '¿Estás seguro de que deseas facturar esta prefactura?'),
        t('confirmModals.invoicePreInvoice.confirm', 'Facturar'),
        t('confirmModals.invoicePreInvoice.cancel', 'Cancelar'),
      );
    },
    [canCreateInvoices, invoicePreInvoice, refetch, t],
  );

  // 🎯 Handler para facturar múltiples prefacturas seleccionadas
  const handleBulkInvoicePreInvoices = useCallback(() => {
    if (!canCreateInvoices) {
      return;
    }
    // Obtener los IDs de las prefacturas seleccionadas
    const selectedIds = Array.from(selectedPreInvoices);

    if (selectedIds.length === 0) {
      return;
    }

    // Mostrar confirmación antes de facturar
    confirmBulkInvoiceModalRef.current?.open(
      () => {
        // Usuario confirmó: ejecutar facturación masiva
        bulkInvoicePreInvoices(selectedIds, () => {
          // Limpiar selección y refetch de datos después de la facturación exitosa
          handleClearSelection();
          refetch();
        });
      },
      undefined,
      t('confirmModals.bulkInvoicePreInvoice.title', 'Facturar prefacturas'),
      t('confirmModals.bulkInvoicePreInvoice.message', {
        count: selectedIds.length,
        defaultValue: `¿Estás seguro de que deseas facturar ${selectedIds.length} prefacturas?`,
      }),
      t('confirmModals.bulkInvoicePreInvoice.confirm', 'Facturar'),
      t('confirmModals.bulkInvoicePreInvoice.cancel', 'Cancelar'),
    );
  }, [canCreateInvoices, selectedPreInvoices, bulkInvoicePreInvoices, handleClearSelection, refetch, t]);

  // 🎯 Handler para eliminar prefactura
  const handleDeletePreInvoice = useCallback(
    (preInvoiceId: number) => {
      // Mostrar modal de confirmación antes de eliminar
      confirmDeleteModalRef.current?.open(
        () => {
          // Usuario confirmó: ejecutar eliminación
          deletePreInvoice(preInvoiceId, () => {
            // Refetch de datos después de la eliminación exitosa
            refetch();
          });
        },
        undefined,
        t('confirmModals.deletePreInvoice.title', 'Eliminar prefactura'),
        t(
          'confirmModals.deletePreInvoice.message',
          '¿Estás seguro de que deseas eliminar esta prefactura? Esta acción liberará los envíos y almacenamientos asociados.',
        ),
        t('confirmModals.deletePreInvoice.confirm', 'Eliminar'),
        t('confirmModals.deletePreInvoice.cancel', 'Cancelar'),
      );
    },
    [deletePreInvoice, refetch, t],
  );

  // 🎯 Handler para combinar prefacturas seleccionadas
  const handleMergePreInvoices = useCallback(() => {
    const selectedIds = Array.from(selectedPreInvoices);
    if (selectedIds.length < 2) return;

    confirmMergeModalRef.current?.open(
      () => {
        mergePreInvoices(selectedIds, () => {
          handleClearSelection();
          refetch();
        });
      },
      undefined,
      t('confirmModals.mergePreInvoices.title', 'Combinar prefacturas'),
      t('confirmModals.mergePreInvoices.message', {
        count: selectedIds.length,
        defaultValue: `¿Estás seguro de que deseas combinar ${selectedIds.length} prefacturas? Se creará una nueva prefactura con todos los envíos/almacenamientos.`,
      }),
      t('confirmModals.mergePreInvoices.confirm', 'Combinar'),
      t('confirmModals.mergePreInvoices.cancel', 'Cancelar'),
    );
  }, [selectedPreInvoices, mergePreInvoices, handleClearSelection, refetch, t]);

  // 🎯 Obtener columnas para el ColumnSelector usando el hook de AG Grid
  const { tanStackColumns } = useAgGridColumns({
    selectedColumns,
    onViewPreInvoice: handleViewPreInvoice,
    onInvoicePreInvoice: handleInvoicePreInvoice,
    onDeletePreInvoice: handleDeletePreInvoice,
  });

  // Columnas para el selector: usar las columnas de AG Grid convertidas a TanStack
  const selectorColumns = useMemo(() => tanStackColumns, [tanStackColumns]);

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad) {
    return <LoadingState message={t('loading.message', 'Cargando prefacturas...')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      {/* Breadcrumb - Navegación */}
      <Breadcrumb />

      {/* Header con filtros */}
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              handleSearch={handleSearch}
              onFilterChange={handleFilterChange}
              columns={selectorColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
              filterConditions={filterConditions}
              hasTeamData={false}
              searchTerm={searchTerm}
            />
          </div>
          <TotalResults total={total} />
        </div>
      </div>

      {/* Estado vacío */}
      {!isInitialLoad && preInvoices.length === 0 && (
        <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.ORDERS} />
      )}

      {/* Tabla visible solo si hay resultados */}
      {!isInitialLoad && total > 0 && (
        <div
          className={`transition-opacity duration-300 mb-4 mt-4 ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
        >
          <PreInvoicesAgGridTable
            {...preInvoicesViewProps}
            preInvoices={preInvoices}
            selectedPreInvoices={selectedPreInvoices}
            selectedColumns={selectedColumns}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onSelectionChanged={handleSelectionChanged}
            onViewPreInvoice={handleViewPreInvoice}
            onInvoicePreInvoice={handleInvoicePreInvoice}
            onDeletePreInvoice={handleDeletePreInvoice}
            onBulkInvoicePreInvoices={handleBulkInvoicePreInvoices}
            onBulkMergePreInvoices={handleMergePreInvoices}
          />
        </div>
      )}

      {/* Modal de confirmación para facturar prefactura individual */}
      <ConfirmModal ref={confirmInvoiceModalRef} />

      {/* Modal de confirmación para facturar prefacturas en bloque */}
      <ConfirmModal ref={confirmBulkInvoiceModalRef} />

      {/* Modal de confirmación para eliminar prefactura */}
      <ConfirmModal ref={confirmDeleteModalRef} />

      {/* Modal de confirmación para combinar prefacturas */}
      <ConfirmModal ref={confirmMergeModalRef} />
    </div>
  );
};
